/**
 * Blog/CMS — Paloma
 * A Modern Travel Journal.
 *
 * Light pastel editorial travel-storytelling platform with a long
 * magazine-style scrolling homepage:
 *   1.  Minimal navbar
 *   2.  Asymmetric editorial hero (polaroid collage)
 *   3.  Illustrated pastel travel-route map
 *   4.  Featured destinations (8 cities)
 *   5.  Travel stories grid (Pinterest-style asymmetry)
 *   6.  Journal highlights (pull quotes)
 *   7.  Photo essay (masonry)
 *   8.  Creator notes
 *   9.  Newsletter
 *  10.  Footer
 *
 * + Full-screen article view (hero, dek, body, pull quote, gallery, related).
 *
 * Typography: Italiana (display) + PT Serif (italic editorial) + DM Sans (body/UI).
 * Pure CSS animations (no framer-motion); Sandpack-safe JSX.
 */

export const BLOG_CMS_FILES = {
  'src/index.css': `@import url('https://fonts.googleapis.com/css2?family=Italiana&family=PT+Serif:ital,wght@0,400;0,700;1,400;1,700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

* { box-sizing: border-box; }
html, body, #root { height: 100%; }
body {
  margin: 0;
  font-family: 'DM Sans', system-ui, sans-serif;
  background: #FBF7F2;
  color: #2A2520;
  -webkit-font-smoothing: antialiased;
}
h1, h2, h3, h4, h5, h6, p { margin: 0; }
input, button, textarea { font-family: inherit; }

.pj-display { font-family: 'Italiana', 'Times New Roman', serif; letter-spacing: 0.005em; }
.pj-editorial { font-family: 'PT Serif', 'Georgia', serif; font-style: italic; }
.pj-editorial-b { font-family: 'PT Serif', 'Georgia', serif; }
.pj-sans { font-family: 'DM Sans', system-ui, sans-serif; }

/* ========================================================
   PASTEL COLOR TOKENS (used via inline style + CSS vars)
   ======================================================== */
:root {
  --pj-ivory: #FBF7F2;
  --pj-cream: #F5EFE6;
  --pj-card: #FFFFFF;
  --pj-border: #EDE3D4;
  --pj-text: #2A2520;
  --pj-text-2: #6B6358;
  --pj-text-3: #A99C8B;
  --pj-peach: #F4C4A1;
  --pj-peach-soft: #FAD9BC;
  --pj-blush: #EBC1BD;
  --pj-blush-soft: #F4D7D2;
  --pj-sage: #C5D5C0;
  --pj-sage-soft: #DCE6D5;
  --pj-sky: #C8D9E0;
  --pj-sky-soft: #DCE7EC;
  --pj-lavender: #D4C5DF;
  --pj-lavender-soft: #E2D5EA;
  --pj-coral: #E9A294;
  --pj-sand: #E8D9C2;
}

/* ========================================================
   SHELL / SCROLL
   ======================================================== */
.pj-shell {
  min-height: 100vh;
  width: 100%;
  background:
    radial-gradient(ellipse 900px 600px at 80% -10%, rgba(244, 196, 161, 0.32), transparent 70%),
    radial-gradient(ellipse 800px 500px at 0% 30%, rgba(235, 193, 189, 0.22), transparent 70%),
    linear-gradient(180deg, #FDFAF5 0%, #FBF7F2 100%);
  overflow-y: auto;
  overflow-x: hidden;
  height: 100vh;
}

.pj-section { padding: 96px 32px; max-width: 1320px; margin: 0 auto; }
@media (max-width: 768px) { .pj-section { padding: 56px 20px; } }

/* ========================================================
   TYPOGRAPHY HELPERS
   ======================================================== */
.pj-eyebrow {
  font-family: 'DM Sans', sans-serif;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: #8A7B66;
}

.pj-headline {
  font-family: 'Italiana', serif;
  font-weight: 400;
  letter-spacing: -0.01em;
  line-height: 1;
  color: var(--pj-text);
}

.pj-body {
  font-family: 'PT Serif', serif;
  font-size: 17px;
  line-height: 1.85;
  color: #4B4339;
}

/* ========================================================
   BUTTONS / CHIPS
   ======================================================== */
.pj-btn {
  display: inline-flex; align-items: center; gap: 10px;
  padding: 13px 26px;
  border-radius: 999px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  cursor: pointer;
  border: none;
  transition: transform .35s cubic-bezier(.2,.85,.35,1), box-shadow .35s ease, background .35s ease, color .35s ease, border-color .35s ease;
  white-space: nowrap;
}
.pj-btn-primary {
  background: #2A2520;
  color: #FBF7F2;
  box-shadow: 0 10px 24px -10px rgba(42, 37, 32, 0.45);
}
.pj-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 16px 30px -12px rgba(42, 37, 32, 0.55); }

.pj-btn-ghost {
  background: transparent;
  color: var(--pj-text);
  border: 1px solid var(--pj-border);
}
.pj-btn-ghost:hover { border-color: var(--pj-text); background: rgba(255, 255, 255, 0.5); }

.pj-btn-soft {
  background: #FFFFFF;
  color: var(--pj-text);
  border: 1px solid var(--pj-border);
  box-shadow: 0 6px 16px -8px rgba(42, 37, 32, 0.18);
}
.pj-btn-soft:hover { transform: translateY(-1.5px); box-shadow: 0 14px 26px -10px rgba(42, 37, 32, 0.25); }

.pj-icon-btn {
  width: 38px; height: 38px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  background: #FFFFFF;
  color: var(--pj-text);
  border: 1px solid var(--pj-border);
  cursor: pointer;
  transition: transform .3s ease, background .3s ease, border-color .3s ease;
}
.pj-icon-btn:hover { transform: translateY(-1.5px); border-color: var(--pj-text); }

.pj-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  font-family: 'DM Sans', sans-serif;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  border: 1px solid var(--pj-border);
  background: rgba(255, 255, 255, 0.7);
  color: #6B6358;
  white-space: nowrap;
}
.pj-chip-warm  { background: rgba(244, 196, 161, 0.25); border-color: rgba(244, 196, 161, 0.55); color: #8A5B36; }
.pj-chip-blush { background: rgba(235, 193, 189, 0.32); border-color: rgba(235, 193, 189, 0.6);  color: #8C5A52; }
.pj-chip-sage  { background: rgba(197, 213, 192, 0.36); border-color: rgba(197, 213, 192, 0.65); color: #4F6948; }
.pj-chip-sky   { background: rgba(200, 217, 224, 0.42); border-color: rgba(200, 217, 224, 0.7);  color: #4A6772; }
.pj-chip-lav   { background: rgba(212, 197, 223, 0.4);  border-color: rgba(212, 197, 223, 0.7);  color: #5E4B70; }
.pj-chip-dark  { background: var(--pj-text); border-color: var(--pj-text); color: var(--pj-ivory); }

/* ========================================================
   CARDS
   ======================================================== */
.pj-card {
  position: relative;
  border-radius: 22px;
  background: #FFFFFF;
  border: 1px solid var(--pj-border);
  overflow: hidden;
  transition: transform .45s cubic-bezier(.2,.85,.35,1), box-shadow .45s ease, border-color .45s ease;
  box-shadow: 0 1px 0 rgba(255,255,255,0.6) inset, 0 12px 30px -22px rgba(42, 37, 32, 0.25);
}
.pj-card-soft {
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}
.pj-card:hover { transform: translateY(-4px); box-shadow: 0 22px 48px -22px rgba(42, 37, 32, 0.35); border-color: rgba(42, 37, 32, 0.18); }

.pj-card-img { position: relative; overflow: hidden; }
.pj-card-img::after {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.18) 0%, transparent 50%);
  pointer-events: none;
}

/* Polaroid */
.pj-polaroid {
  background: #FFFFFF;
  padding: 14px 14px 20px;
  border-radius: 6px;
  box-shadow: 0 18px 36px -20px rgba(42, 37, 32, 0.4);
  border: 1px solid rgba(237, 227, 212, 0.7);
}

/* ========================================================
   ANIMATIONS — soft, calm, editorial
   ======================================================== */
@keyframes pj-rise {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes pj-fade-in {
  from { opacity: 0; } to { opacity: 1; }
}
@keyframes pj-zoom-in {
  from { opacity: 0; transform: scale(0.96); }
  to   { opacity: 1; transform: scale(1); }
}
@keyframes pj-float-a {
  0%, 100% { transform: rotate(-4deg) translateY(0); }
  50%      { transform: rotate(-4deg) translateY(-6px); }
}
@keyframes pj-float-b {
  0%, 100% { transform: rotate(3deg) translateY(0); }
  50%      { transform: rotate(3deg) translateY(-5px); }
}
@keyframes pj-float-c {
  0%, 100% { transform: rotate(-1.5deg) translateY(0); }
  50%      { transform: rotate(-1.5deg) translateY(-4px); }
}
@keyframes pj-dash {
  to { stroke-dashoffset: -140; }
}
@keyframes pj-pulse {
  0%, 100% { transform: scale(1); opacity: 0.55; }
  50%      { transform: scale(1.6); opacity: 0; }
}
@keyframes pj-shimmer {
  0%, 100% { opacity: 0.7; }
  50%      { opacity: 1; }
}
@keyframes pj-marquee {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}

.pj-rise         { animation: pj-rise .8s cubic-bezier(.2,.85,.25,1) both; }
.pj-rise-1       { animation-delay: 0.06s; }
.pj-rise-2       { animation-delay: 0.14s; }
.pj-rise-3       { animation-delay: 0.24s; }
.pj-rise-4       { animation-delay: 0.34s; }
.pj-rise-5       { animation-delay: 0.44s; }
.pj-rise-6       { animation-delay: 0.54s; }
.pj-zoom-in      { animation: pj-zoom-in .9s cubic-bezier(.2,.85,.25,1) both; }
.pj-fade-in      { animation: pj-fade-in .6s ease both; }

.pj-float-a      { animation: pj-float-a 7s ease-in-out infinite; }
.pj-float-b      { animation: pj-float-b 8s ease-in-out infinite; }
.pj-float-c      { animation: pj-float-c 6.5s ease-in-out infinite; }

.pj-route-flow {
  fill: none;
  stroke-width: 1.5;
  stroke-dasharray: 1.5 7;
  stroke-linecap: round;
  animation: pj-dash 16s linear infinite;
}
.pj-pin-pulse { transform-origin: center; animation: pj-pulse 2.6s ease-out infinite; transform-box: fill-box; }
.pj-shimmer   { animation: pj-shimmer 3.2s ease-in-out infinite; }

/* ========================================================
   ARTICLE VIEW
   ======================================================== */
@keyframes pj-article-in {
  from { opacity: 0; transform: scale(1.012); }
  to   { opacity: 1; transform: scale(1); }
}
.pj-article-in { animation: pj-article-in .55s cubic-bezier(.2,.85,.25,1) both; }

/* ========================================================
   SCROLLBAR
   ======================================================== */
.pj-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.pj-scroll::-webkit-scrollbar-track { background: transparent; }
.pj-scroll::-webkit-scrollbar-thumb { background: rgba(169, 156, 139, 0.35); border-radius: 999px; }
.pj-scroll::-webkit-scrollbar-thumb:hover { background: rgba(169, 156, 139, 0.6); }

/* Helpers */
.pj-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.pj-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.pj-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.pj-clamp-4 { display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
`,

  'src/App.js': `import React, { useState } from 'react';
import './index.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import RouteSection from './components/RouteSection';
import Destinations from './components/Destinations';
import StoriesGrid from './components/StoriesGrid';
import JournalHighlights from './components/JournalHighlights';
import PhotoEssay from './components/PhotoEssay';
import CreatorNotes from './components/CreatorNotes';
import Newsletter from './components/Newsletter';
import Footer from './components/Footer';
import ArticleView from './components/ArticleView';
import { DESTINATIONS } from './data/paloma';

export default function App() {
  const [activeId, setActiveId] = useState(null);
  const active = DESTINATIONS.find((d) => d.id === activeId) || null;

  return (
    <div className="pj-shell pj-scroll">
      <Navbar />
      <Hero onOpen={setActiveId} />
      <RouteSection onSelect={setActiveId} />
      <Destinations onSelect={setActiveId} />
      <StoriesGrid onSelect={setActiveId} />
      <JournalHighlights />
      <PhotoEssay />
      <CreatorNotes />
      <Newsletter />
      <Footer />

      {active && (
        <ArticleView destination={active} onClose={() => setActiveId(null)} onSelect={setActiveId} />
      )}
    </div>
  );
}
`,

  'src/data/paloma.js': `/** Paloma — Modern Travel Journal data */

export const CATEGORIES = [
  { id: 'all',       label: 'All journals' },
  { id: 'cities',    label: 'Cities' },
  { id: 'coast',     label: 'Coast & Islands' },
  { id: 'mountain',  label: 'Mountain' },
  { id: 'culture',   label: 'Culture' },
  { id: 'food',      label: 'Food' },
];

export const NAV_LINKS = ['Journals', 'Destinations', 'Photo Essays', 'About'];

export const DESTINATIONS = [
  {
    id: 'paris',     name: 'Paris',        country: 'France',      hero: 'paris',
    category: 'cities',  tone: 'blush',
    chip: 'Cities',  eyebrow: 'Issue 12 · Slow city',
    season: 'Spring · May',
    teaser: 'Three days on the slow side of the Left Bank — coffee, bookstores, and the small museum nobody talks about.',
    article: {
      title: 'Three Days on the Slow Side of Paris.',
      dek: 'A patient itinerary for the rive gauche: secondary museums, sun on stone, and the bakeries the locals walk to.',
      author: 'Mira Vance', role: 'Senior Editor',
      date: 'May 24, 2026', readMin: 11,
      body: [
        'You are supposed to arrive in Paris and feel something immediate. A great deal of writing has been done about this. In practice you arrive on the train from Charles de Gaulle and the first thing you see is a man eating a sandwich on the platform, and the second thing you see is the rain on Boulevard Magenta, and the third thing you do is wait nine extra minutes for the next Metro because the first one is full.',
        'This is not a complaint. The patience the city asks of you on arrival is, it turns out, the same patience it asks of you for the next seventy-two hours, and the patience is the entire reason you came.',
        'On the second morning you walk from Saint-Sulpice down to the river. You stop at three bakeries on the way. You eat the second of three croissants standing on the Pont des Arts and you watch a barge come down the river with two children sitting on the roof.',
      ],
      pullQuote: 'Paris is not a city you visit in three days. It is a city you sit inside for three days.',
      pullQuoteBy: 'Élise Bonnard, bookseller, 6th',
      highlights: [
        { title: 'Eat',   text: 'Du Pain et des Idées at 11:00. Pistachio escargot.' },
        { title: 'Walk',  text: 'Pont Neuf to Saint-Germain. Slowly. Twice.' },
        { title: 'See',   text: 'Musée Eugène Delacroix — a courtyard the size of a postage stamp.' },
        { title: 'Drink', text: 'Marigold for natural wine. Five tables.' },
      ],
      gallery: [
        { tone: 'blush',    label: 'Saint-Sulpice, 09:14' },
        { tone: 'cream',    label: 'Rue de Seine bakery' },
        { tone: 'peach',    label: 'Pont des Arts, noon' },
        { tone: 'sage',     label: 'Jardin du Luxembourg' },
      ],
      related: ['santorini', 'tokyo'],
    },
  },
  {
    id: 'tokyo',     name: 'Tokyo',        country: 'Japan',       hero: 'tokyo',
    category: 'cities',  tone: 'blush',
    chip: 'Cities',  eyebrow: 'Issue 11 · Small streets',
    season: 'Spring · April',
    teaser: 'The slowest neighborhood in the world\\u2019s fastest city. A Tuesday in Shimokitazawa.',
    article: {
      title: 'A Tuesday in Shimokitazawa.',
      dek: 'Forty-two vinyl shops, a cat in every window, and the standing-curry counter behind the bookshop.',
      author: 'Yuki Tanaka', role: 'Tokyo Correspondent',
      date: 'May 18, 2026', readMin: 9,
      body: [
        'Shimokitazawa wakes up at eleven and goes to bed at three. Most of the vinyl shops are run by one person and a cat, and the cat is, almost without exception, asleep in the window.',
        'You take the Inokashira line two stops from Shibuya. You walk south. You drink coffee at Bear Pond Espresso. You spend an hour, then two, then three, in the basement of Disk Union, and you do not buy anything until the very last hour, and what you buy is not what you came in to look for.',
      ],
      pullQuote: 'In Shimokitazawa, the records do not just play. They wait for you.',
      pullQuoteBy: 'Mr. Ito, Flash Disc Ranch, est. 1991',
      highlights: [
        { title: 'Open',  text: 'Disk Union basement, 12:00. Get there for the sort.' },
        { title: 'Eat',   text: 'Standing-curry counter behind Books Honyaradou.' },
        { title: 'Stay',  text: 'The Mustard Hotel \\u2014 above a record shop.' },
        { title: 'Walk',  text: 'Through to the south exit. Cross the footbridge.' },
      ],
      gallery: [
        { tone: 'blush',    label: 'Shimokitazawa, 13:42' },
        { tone: 'peach',    label: 'Disk Union, basement' },
        { tone: 'sage',     label: 'South-side curry counter' },
        { tone: 'cream',    label: 'The footbridge' },
      ],
      related: ['seoul', 'paris'],
    },
  },
  {
    id: 'seoul',     name: 'Seoul',        country: 'South Korea', hero: 'seoul',
    category: 'culture', tone: 'lavender',
    chip: 'Culture', eyebrow: 'Issue 10 · Old roofs',
    season: 'Spring · April',
    teaser: 'A morning between two palaces. The neighborhood of nine hundred hanok.',
    article: {
      title: 'Hanok Mornings in Bukchon.',
      dek: 'The traditional houses between Gyeongbokgung and Changdeokgung are where Seoul\\u2019s quietest design studios have moved.',
      author: 'Ji-eun Park', role: 'Asia Editor',
      date: 'May 12, 2026', readMin: 8,
      body: [
        'Bukchon is the quietest neighborhood in central Seoul. Nine hundred hanok \\u2014 traditional courtyard houses with curved tile roofs \\u2014 sit between two royal palaces in a tangle of alleys steep enough to keep most of the city away.',
        'Behind half-a-dozen of the doorways on Gyedong-gil are some of the most ambitious small design studios in Asia: ceramicists, perfumers, jewellers, two architecture firms, one bookshop that sells one book at a time.',
      ],
      pullQuote: 'A hanok is not a museum. It is a working room that has been listening for four hundred years.',
      pullQuoteBy: 'Architect Cho Min-su',
      highlights: [
        { title: 'Walk',   text: 'Anguk station exit 2. Climb to Bukchon viewpoint.' },
        { title: 'Eat',    text: 'Onion Anguk \\u2014 bakery inside a restored hanok.' },
        { title: 'See',    text: 'Studio Concrete pottery showroom. Knock first.' },
        { title: 'Dinner', text: 'Jjajangmyeon at Gaehwa in Insadong.' },
      ],
      gallery: [
        { tone: 'lavender', label: 'Bukchon rooftops' },
        { tone: 'peach',    label: 'Onion Anguk' },
        { tone: 'cream',    label: 'Studio Concrete' },
        { tone: 'sage',     label: 'Hanok courtyard' },
      ],
      related: ['tokyo', 'bali'],
    },
  },
  {
    id: 'bali',      name: 'Bali',         country: 'Indonesia',   hero: 'bali',
    category: 'coast',   tone: 'sage',
    chip: 'Coast & Islands', eyebrow: 'Issue 12 · The slow island',
    season: 'Dry season · July',
    teaser: 'Five mornings in Ubud. Rice terraces, slow coffee, and a workshop above a temple.',
    article: {
      title: 'Five Mornings in Ubud.',
      dek: 'A small notebook of mornings — the river, the rice, the rooftop where the warung opens at seven.',
      author: 'Aru Tan', role: 'Senior Editor',
      date: 'May 2, 2026', readMin: 9,
      body: [
        'Ubud at six in the morning is birdsong and the soft tongue-clicking of someone walking ducks down the lane. The light has not yet decided what it wants to be. The river smells of warm stone.',
        'You stay above the cafés on Jalan Bisma. You walk every morning the same way: north to the rice terraces, then east to the warung above the temple, then back along the river. The walk takes an hour. You do it five times.',
      ],
      pullQuote: 'Bali does not ask you to slow down. It just makes hurry impossible.',
      pullQuoteBy: 'Made Sukma, ceramicist, Ubud',
      highlights: [
        { title: 'Stay',   text: 'A guesthouse on Jalan Bisma with a verandah.' },
        { title: 'Walk',   text: 'Campuhan Ridge before sunrise. No music.' },
        { title: 'Eat',    text: 'The warung above Pura Taman Saraswati.' },
        { title: 'Visit',  text: 'Sukma\\u2019s ceramic studio in Penestanan.' },
      ],
      gallery: [
        { tone: 'sage',     label: 'Campuhan Ridge, 05:54' },
        { tone: 'peach',    label: 'Warung counter' },
        { tone: 'cream',    label: 'Rice terraces' },
        { tone: 'blush',    label: 'Pura Taman Saraswati' },
      ],
      related: ['santorini', 'switzerland'],
    },
  },
  {
    id: 'santorini', name: 'Santorini',    country: 'Greece',      hero: 'santorini',
    category: 'coast',   tone: 'sky',
    chip: 'Coast & Islands', eyebrow: 'Issue 11 · A quieter caldera',
    season: 'Late spring · June',
    teaser: 'The villages on the other side of the caldera. Whitewash, almonds, and one perfect harbour at six.',
    article: {
      title: 'The Quieter Caldera.',
      dek: 'Pyrgos, Megalochori, and the small harbour at Ammoudi. A Santorini that has not been photographed for ten years.',
      author: 'Sofia Marchetti', role: 'Mediterranean Editor',
      date: 'April 28, 2026', readMin: 10,
      body: [
        'The trick is to drive the wrong way. Oia is, of course, where everyone goes for sunset. Fira is where everyone eats dinner. The way to fall in love with Santorini is to leave both.',
        'In Pyrgos the alleys are too narrow for cars. The light is the same light, but it lands on whitewash that hasn\\u2019t been painted for the tourists. You can sit at a kafenio on the square for an hour, and the only person who notices you is the cat under the chair.',
      ],
      pullQuote: 'The Cyclades are eighteen islands. Tourists know two of them.',
      pullQuoteBy: 'Yannis Petros, taverna keeper, Pyrgos',
      highlights: [
        { title: 'Stay',  text: 'A cave room in Pyrgos. Two streets from the square.' },
        { title: 'Eat',   text: 'Selene at the old monastery. Book ten days out.' },
        { title: 'Swim',  text: 'Ammoudi harbour. After the last boat leaves.' },
        { title: 'Walk',  text: 'The footpath from Pyrgos to Megalochori at sunset.' },
      ],
      gallery: [
        { tone: 'sky',      label: 'Pyrgos, 18:48' },
        { tone: 'cream',    label: 'Whitewashed alley' },
        { tone: 'blush',    label: 'Kafenio table' },
        { tone: 'lavender', label: 'Ammoudi harbour' },
      ],
      related: ['paris', 'switzerland'],
    },
  },
  {
    id: 'dubai',     name: 'Dubai',        country: 'UAE',         hero: 'dubai',
    category: 'culture', tone: 'peach',
    chip: 'Culture', eyebrow: 'Issue 12 · A walking city',
    season: 'Cool season · March',
    teaser: 'Al Fahidi, the old town quarter. Coffee in a courtyard, an abra across the creek, sunset over Deira.',
    article: {
      title: 'A Walking City in the Desert.',
      dek: 'Dubai\\u2019s old town has, quietly, become one of the best walking neighborhoods in the Gulf.',
      author: 'Rami Khoury', role: 'Senior Correspondent',
      date: 'April 18, 2026', readMin: 8,
      body: [
        'The shaded alleys of Al Fahidi are narrow on purpose. The walls are seven meters tall on purpose. The wind towers \\u2014 the barjeel \\u2014 that catch the breeze and funnel it down into the courtyards have been there, in one form or another, for two hundred years.',
        'What is new is everything around them. There are bookshops now. There are coffee shops with three tables. There is a museum of perfume. You walk at five, you cross the creek at seven, you walk Deira until it is dark.',
      ],
      pullQuote: 'The old town is the only place in Dubai where shadows are part of the architecture.',
      pullQuoteBy: 'Lujain N., urban planner, DXB',
      highlights: [
        { title: 'Start',   text: 'Al Fahidi metro station, exit 1. Walk west.' },
        { title: 'Coffee',  text: 'Arabian Tea House courtyard, late afternoon.' },
        { title: 'Cross',   text: 'Abra across to Deira. One dirham. Sunset.' },
        { title: 'Eat',     text: 'Logma in City Walk for late dinner.' },
      ],
      gallery: [
        { tone: 'peach',    label: 'Al Fahidi alleys' },
        { tone: 'sand',     label: 'Wind tower courtyard' },
        { tone: 'cream',    label: 'Abra crossing' },
        { tone: 'blush',    label: 'Deira souk' },
      ],
      related: ['paris', 'seoul'],
    },
  },
  {
    id: 'switzerland', name: 'Lauterbrunnen', country: 'Switzerland', hero: 'switzerland',
    category: 'mountain', tone: 'sky',
    chip: 'Mountain', eyebrow: 'Issue 11 · A green valley',
    season: 'Late summer · September',
    teaser: 'Seventy-two waterfalls in a single valley. A guesthouse with a window onto two of them.',
    article: {
      title: 'Seventy-Two Waterfalls.',
      dek: 'Lauterbrunnen at the slowest end of summer: train, valley, milk, and one extremely cold lake.',
      author: 'Helena Ostler', role: 'Editor-in-Chief',
      date: 'April 9, 2026', readMin: 10,
      body: [
        'You take the train from Bern. You change at Interlaken Ost. The valley opens in front of you in a way that makes the camera in your bag feel briefly absurd.',
        'There are seventy-two waterfalls in this single valley, and at least four of them are visible from any guesthouse window on the eastern side of the village. You stay three nights. You walk to two of them.',
      ],
      pullQuote: 'You do not look at the Lauterbrunnen valley. You stand inside it.',
      pullQuoteBy: 'Karin Hess, mountain guide, Wengen',
      highlights: [
        { title: 'Train',   text: 'Bern to Lauterbrunnen via Interlaken Ost.' },
        { title: 'Stay',    text: 'Hotel Oberland \\u2014 east-facing rooms only.' },
        { title: 'Walk',    text: 'Trümmelbach falls then up to Mürren.' },
        { title: 'Swim',    text: 'Oeschinensee. Late afternoon. Painful.' },
      ],
      gallery: [
        { tone: 'sky',      label: 'Lauterbrunnen, 07:14' },
        { tone: 'sage',     label: 'Trümmelbach' },
        { tone: 'cream',    label: 'Wengen hut' },
        { tone: 'lavender', label: 'Oeschinensee' },
      ],
      related: ['bali', 'santorini'],
    },
  },
  {
    id: 'new-york',  name: 'New York',     country: 'USA',         hero: 'new-york',
    category: 'cities', tone: 'lavender',
    chip: 'Cities', eyebrow: 'Issue 12 · Across the river',
    season: 'Late spring · May',
    teaser: 'Twelve hours in Greenpoint. Take the G to Nassau and walk slowly north.',
    article: {
      title: 'Twelve Hours in Greenpoint.',
      dek: 'The Brooklyn neighborhood Manhattan stopped paying attention to is, predictably, the best place to eat in the city this year.',
      author: 'Jules Okereke', role: 'New York Correspondent',
      date: 'April 1, 2026', readMin: 10,
      body: [
        'Take the G to Nassau and walk north. Take your time. The neighborhood does not, generally speaking, hurry, and there are eight blocks between you and breakfast.',
        'Greenpoint has been the Polish neighborhood in Brooklyn for ninety years. It is now, simultaneously, one of the most interesting food neighborhoods in the United States, which is to say that you can have pierogi for breakfast and Filipino kamayan for dinner and a fourteen-dollar cocktail in between.',
      ],
      pullQuote: 'Greenpoint is Brooklyn for people who have stopped trying to impress Manhattan.',
      pullQuoteBy: 'B. Park, chef-owner, Bonnie\\u2019s',
      highlights: [
        { title: 'Breakfast', text: 'Karczma for the Polish plate. 09:00.' },
        { title: 'Coffee',    text: 'Devoción on Greenpoint Ave. Back room.' },
        { title: 'Dinner',    text: 'Bonnie\\u2019s. Book three weeks out.' },
        { title: 'Drink',     text: 'Achilles Heel \\u2014 wine, candles, no music.' },
      ],
      gallery: [
        { tone: 'lavender', label: 'Manhattan Ave, 08:42' },
        { tone: 'peach',    label: 'Karczma plate' },
        { tone: 'blush',    label: 'East River sunset' },
        { tone: 'cream',    label: 'Achilles Heel' },
      ],
      related: ['paris', 'tokyo'],
    },
  },
];

/** Approximate flat-map coordinates (viewBox 0 0 1600 760) for the route section. */
export const MAP_POINTS = [
  { id: 'new-york',    x: 360,  y: 320 },
  { id: 'paris',       x: 800,  y: 280 },
  { id: 'santorini',   x: 870,  y: 360 },
  { id: 'dubai',       x: 970,  y: 410 },
  { id: 'switzerland', x: 815,  y: 310 },
  { id: 'bali',        x: 1230, y: 540 },
  { id: 'seoul',       x: 1310, y: 350 },
  { id: 'tokyo',       x: 1370, y: 370 },
];

export const ROUTE_PATH = 'M 360 320 C 540 220, 720 220, 800 280 S 850 340, 870 360 S 940 400, 970 410 S 1080 480, 1230 540 S 1290 380, 1310 350 S 1350 360, 1370 370';

export const HERO_TICKER = [
  'Currently on the road \\u2014 Lisbon',
  'Volume XII \\u00B7 Spring 2026',
  'Twelve writers \\u00B7 eight cities',
  'New issue every Friday morning',
];

export const HIGHLIGHTS = [
  {
    quote: 'The patience the city asks of you on arrival is the same patience it asks of you for the next seventy-two hours, and the patience is the entire reason you came.',
    by: 'Mira Vance', city: 'Paris', tone: 'blush',
  },
  {
    quote: 'Bali does not ask you to slow down. It just makes hurry impossible.',
    by: 'Aru Tan', city: 'Bali', tone: 'sage',
  },
  {
    quote: 'You do not look at the Lauterbrunnen valley. You stand inside it.',
    by: 'Helena Ostler', city: 'Switzerland', tone: 'sky',
  },
];

export const PHOTO_ESSAY = [
  { tone: 'peach',    h: 280, label: 'Saint-Sulpice, 09:14' },
  { tone: 'sage',     h: 340, label: 'Campuhan Ridge, 05:54' },
  { tone: 'sky',      h: 240, label: 'Pyrgos, 18:48' },
  { tone: 'blush',    h: 320, label: 'Disk Union, basement' },
  { tone: 'lavender', h: 260, label: 'Manhattan Ave, 08:42' },
  { tone: 'cream',    h: 300, label: 'Trümmelbach' },
  { tone: 'peach',    h: 220, label: 'Arabian Tea House' },
  { tone: 'sky',      h: 280, label: 'Oeschinensee' },
];

export const CREATORS = [
  {
    name: 'Mira Vance',     role: 'Senior Editor, Cities',  tone: 'blush',
    note: 'In Paris, again, until the end of June. Working on a small book about the second-best bakeries on every street.',
    place: 'Paris, France',
  },
  {
    name: 'Aru Tan',        role: 'Senior Editor, Islands', tone: 'sage',
    note: 'Five mornings in Ubud, walking the same loop. I have decided this is the entire job description.',
    place: 'Bali, Indonesia',
  },
  {
    name: 'Helena Ostler',  role: 'Editor-in-Chief',         tone: 'sky',
    note: 'Letter from Lauterbrunnen \\u2014 seventy-two waterfalls and one cold lake. Issue XII opens here.',
    place: 'Lauterbrunnen, CH',
  },
  {
    name: 'Yuki Tanaka',    role: 'Tokyo Correspondent',     tone: 'lavender',
    note: 'A Tuesday in Shimokitazawa. There were forty-two record shops. I made it into seven.',
    place: 'Tokyo, Japan',
  },
];
`,

  'src/components/Icon.js': `import React from 'react';

export default function Icon({ name, size = 16, stroke = 1.4, className = '' }) {
  const p = {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: stroke,
    strokeLinecap: 'round', strokeLinejoin: 'round', className,
  };
  switch (name) {
    case 'arrow':      return <svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    case 'arrow-up':   return <svg {...p}><path d="M7 17 17 7M9 7h8v8" /></svg>;
    case 'search':     return <svg {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
    case 'close':      return <svg {...p}><path d="m6 6 12 12M18 6 6 18" /></svg>;
    case 'pin':        return <svg {...p}><path d="M12 22s-7-7.5-7-13a7 7 0 0 1 14 0c0 5.5-7 13-7 13z" /><circle cx="12" cy="9" r="2.4" /></svg>;
    case 'chev-right': return <svg {...p}><path d="m9 6 6 6-6 6" /></svg>;
    case 'chev-left':  return <svg {...p}><path d="m15 6-6 6 6 6" /></svg>;
    case 'star':       return <svg {...p}><polygon points="12 2 15 9 22 9 17 14 19 22 12 18 5 22 7 14 2 9 9 9" /></svg>;
    case 'compass':    return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-2.5 7-7 2.5 2.5-7 7-2.5z" /></svg>;
    case 'book':       return <svg {...p}><path d="M4 4h11a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4z" /><path d="M4 17h14" /></svg>;
    case 'mail':       return <svg {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>;
    case 'leaf':       return <svg {...p}><path d="M11 20A7 7 0 0 1 4 13c0-2 1-7 1-7s5 0 7 1a7 7 0 0 1 7 7" /><path d="M4 19c1-3 5-6 9-7" /></svg>;
    case 'bookmark':   return <svg {...p}><path d="M6 4h12v17l-6-4-6 4V4z" /></svg>;
    case 'share':      return <svg {...p}><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="M8 11l8-4M8 13l8 4" /></svg>;
    case 'clock':      return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
    case 'menu':       return <svg {...p}><path d="M4 7h16M4 12h16M4 17h16" /></svg>;
    case 'sun':        return <svg {...p}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></svg>;
    case 'plus':       return <svg {...p}><path d="M12 5v14M5 12h14" /></svg>;
    case 'instagram':  return <svg {...p}><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" stroke="none" /></svg>;
    case 'twitter':    return <svg {...p}><path d="M22 5.8a8 8 0 0 1-2.4.7 4 4 0 0 0 1.8-2.2 8 8 0 0 1-2.6 1 4 4 0 0 0-6.9 3.6A11.4 11.4 0 0 1 3 4.7a4 4 0 0 0 1.2 5.3A4 4 0 0 1 2.6 9.5v0a4 4 0 0 0 3.2 4 4 4 0 0 1-1.8.1 4 4 0 0 0 3.8 2.8A8 8 0 0 1 2 18a11.4 11.4 0 0 0 6.2 1.8c7.4 0 11.5-6.2 11.5-11.5v-.5A8.2 8.2 0 0 0 22 5.8z" /></svg>;
    case 'pinterest':  return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7c-2.5 0-4 2-4 4.2 0 1 .5 2 1.4 2.4l.2-.7c-.2-.2-.5-.7-.5-1.5 0-1.8 1.4-3.5 3.5-3.5 1.9 0 2.9 1.2 2.9 2.7 0 2-1 3.6-2.4 3.6-.8 0-1.4-.6-1.2-1.4l.7-2.4c.1-.4-.1-.8-.5-.8-.6 0-1 .6-1 1.4 0 .5.2 1 .2 1L9.8 17c-.2.5-.2 1.7 0 2.2 0 .1.1.1.2 0 .3-.4 1.2-1.4 1.7-3.4l.4-1.5c.4.7 1.4 1.2 2.5 1.2 2.5 0 4.4-2.4 4.4-5.4 0-2.6-2.1-4.5-4.8-4.5z" /></svg>;
    default: return null;
  }
}
`,

  'src/components/Navbar.js': `import React from 'react';
import Icon from './Icon';
import { NAV_LINKS, HERO_TICKER } from '../data/paloma';

export default function Navbar() {
  return (
    <div className="sticky top-0 z-40">
      {/* Top ticker */}
      <div className="overflow-hidden border-b border-[var(--pj-border)] bg-[var(--pj-text)]">
        <div className="flex items-center gap-12 px-6 py-1.5 text-[#FBF7F2]" style={{ animation: 'pj-marquee 36s linear infinite', whiteSpace: 'nowrap' }}>
          {HERO_TICKER.concat(HERO_TICKER).map((t, i) => (
            <span key={i} className="pj-sans flex items-center gap-3 text-[11px] tracking-[0.18em] uppercase">
              <span className="h-1 w-1 rounded-full bg-[var(--pj-peach)] inline-block" />
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Main nav */}
      <header className="border-b border-[var(--pj-border)] bg-[rgba(251,247,242,0.88)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1320px] items-center justify-between gap-8 px-6 md:px-8" style={{ height: 78 }}>
          <a href="#home" className="pj-display text-[26px] tracking-[0.12em] text-[var(--pj-text)]">
            Paloma
            <span className="pj-sans ml-3 hidden text-[10px] uppercase tracking-[0.32em] text-[#A99C8B] md:inline">A Modern Travel Journal</span>
          </a>

          <nav className="hidden items-center gap-9 md:flex">
            {NAV_LINKS.map((n) => (
              <a key={n} href={'#' + n} className="pj-sans text-[13.5px] font-medium text-[var(--pj-text-2)] transition hover:text-[var(--pj-text)]">{n}</a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button type="button" className="pj-icon-btn" aria-label="Search"><Icon name="search" size={15} /></button>
            <button type="button" className="pj-btn pj-btn-primary">
              The journal <Icon name="arrow" size={13} />
            </button>
          </div>
        </div>
      </header>
    </div>
  );
}
`,

  'src/components/Hero.js': `import React from 'react';
import Icon from './Icon';
import { DESTINATIONS } from '../data/paloma';

/** Soft pastel polaroid scenes — one tiny illustrated SVG per destination. */
function PolaroidScene({ kind }) {
  if (kind === 'paris') {
    return (
      <svg viewBox="0 0 200 240" className="block h-full w-full" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="ps-paris-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F4D7D2" /><stop offset="100%" stopColor="#FAD9BC" />
          </linearGradient>
        </defs>
        <rect width="200" height="240" fill="url(#ps-paris-sky)" />
        <circle cx="160" cy="50" r="22" fill="#F4C4A1" opacity="0.7" />
        <g fill="#EBC1BD" opacity="0.55">
          <path d="M0 200 Q 60 170 120 190 T 200 180 L 200 240 L 0 240 Z" />
        </g>
        {/* Eiffel */}
        <g transform="translate(70, 60)" fill="#8C5A52" opacity="0.92">
          <path d="M0 160 L25 0 L50 160 Z" />
          <rect x="10" y="120" width="30" height="5" />
          <rect x="14" y="80" width="22" height="5" />
          <rect x="18" y="40" width="14" height="4" />
        </g>
        {/* Hausmann silhouettes */}
        <g fill="#A99C8B" opacity="0.6">
          <rect x="0"   y="180" width="36" height="60" />
          <rect x="36"  y="170" width="30" height="70" />
          <rect x="140" y="175" width="34" height="65" />
          <rect x="174" y="170" width="26" height="70" />
        </g>
      </svg>
    );
  }
  if (kind === 'santorini') {
    return (
      <svg viewBox="0 0 200 240" className="block h-full w-full" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="ps-sant-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C8D9E0" /><stop offset="100%" stopColor="#FAEFE2" />
          </linearGradient>
        </defs>
        <rect width="200" height="240" fill="url(#ps-sant-sky)" />
        <circle cx="40" cy="50" r="18" fill="#F4C4A1" opacity="0.65" />
        {/* Sea */}
        <rect x="0" y="170" width="200" height="70" fill="#C8D9E0" opacity="0.6" />
        <path d="M 0 170 Q 50 175 100 170 T 200 170" stroke="#FFFFFF" strokeWidth="0.6" fill="none" opacity="0.6" />
        {/* White cubes */}
        <g fill="#FFFFFF">
          <rect x="20" y="120" width="40" height="60" />
          <rect x="62" y="100" width="46" height="80" />
          <rect x="112" y="130" width="42" height="50" />
          <rect x="156" y="115" width="40" height="65" />
        </g>
        {/* Blue domes */}
        <g fill="#4A6772" opacity="0.85">
          <ellipse cx="40" cy="120" rx="20" ry="10" />
          <ellipse cx="86" cy="100" rx="22" ry="10" />
          <ellipse cx="135" cy="130" rx="20" ry="10" />
          <ellipse cx="176" cy="115" rx="20" ry="10" />
        </g>
      </svg>
    );
  }
  if (kind === 'bali') {
    return (
      <svg viewBox="0 0 200 240" className="block h-full w-full" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="ps-bali-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F4D7D2" /><stop offset="100%" stopColor="#DCE6D5" />
          </linearGradient>
        </defs>
        <rect width="200" height="240" fill="url(#ps-bali-sky)" />
        <circle cx="150" cy="60" r="26" fill="#FAD9BC" opacity="0.7" />
        {/* Terrace fields */}
        <g fill="#C5D5C0">
          <path d="M0 180 L200 170 L200 240 L0 240 Z" />
          <path d="M0 160 L200 150 L200 175 L0 185 Z" opacity="0.7" />
          <path d="M0 140 L200 130 L200 155 L0 165 Z" opacity="0.5" />
        </g>
        {/* Palms */}
        <g fill="#6B6358" opacity="0.78">
          <rect x="48" y="120" width="2" height="80" />
          <path d="M49 120 Q 30 110 22 120 Z" />
          <path d="M49 120 Q 68 108 76 122 Z" />
          <path d="M49 120 Q 38 100 32 90 Z" />
          <path d="M49 120 Q 60 98 66 88 Z" />
        </g>
        <g fill="#6B6358" opacity="0.78">
          <rect x="148" y="135" width="2" height="65" />
          <path d="M149 135 Q 130 125 122 138 Z" />
          <path d="M149 135 Q 168 122 178 136 Z" />
          <path d="M149 135 Q 138 118 132 108 Z" />
        </g>
      </svg>
    );
  }
  if (kind === 'tokyo') {
    return (
      <svg viewBox="0 0 200 240" className="block h-full w-full" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="ps-tok-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F4D7D2" /><stop offset="100%" stopColor="#FAD9BC" />
          </linearGradient>
        </defs>
        <rect width="200" height="240" fill="url(#ps-tok-sky)" />
        <circle cx="148" cy="58" r="20" fill="#E9A294" opacity="0.7" />
        {/* Tokyo Tower */}
        <g transform="translate(96, 80)" fill="#8C5A52">
          <path d="M0 150 L15 0 L30 150 Z" />
          <rect x="6" y="115" width="18" height="5" />
          <rect x="8" y="75" width="14" height="5" />
        </g>
        {/* Buildings */}
        <g fill="#A99C8B" opacity="0.65">
          <rect x="0"   y="150" width="40" height="90" />
          <rect x="40"  y="130" width="28" height="110" />
          <rect x="68"  y="160" width="22" height="80" />
          <rect x="130" y="140" width="28" height="100" />
          <rect x="158" y="120" width="22" height="120" />
          <rect x="180" y="155" width="20" height="85" />
        </g>
        {/* Sakura petals */}
        <g fill="#EBC1BD" opacity="0.85">
          <circle cx="30" cy="40" r="2" />
          <circle cx="60" cy="20" r="2.5" />
          <circle cx="90" cy="40" r="2" />
          <circle cx="180" cy="30" r="2.5" />
        </g>
      </svg>
    );
  }
  return null;
}

export default function Hero({ onOpen }) {
  return (
    <section id="home" className="relative overflow-hidden">
      <div className="pj-section flex flex-col items-start gap-12 lg:flex-row lg:items-center lg:gap-14">
        {/* Left — editorial copy */}
        <div className="flex-1 min-w-0">
          <div className="pj-rise pj-rise-1 flex items-center gap-3">
            <span className="pj-eyebrow">Volume XII · Spring 2026</span>
            <span className="h-px w-12 bg-[var(--pj-text-3)]" />
            <span className="pj-sans text-[12px] text-[var(--pj-text-3)]">Issued every Friday</span>
          </div>

          <h1 className="pj-headline pj-rise pj-rise-2 mt-7 text-[64px] sm:text-[88px] md:text-[108px] lg:text-[126px]">
            Notes from the<br/>
            <span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>slow road.</span>
          </h1>

          <p className="pj-rise pj-rise-3 mt-7 max-w-[560px] pj-editorial text-[19px] leading-[1.55] text-[var(--pj-text-2)]">
            A modern travel journal for the people who keep going back. Eight cities, twelve writers, one
            very long letter from spring.
          </p>

          <div className="pj-rise pj-rise-4 mt-9 flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => onOpen('paris')} className="pj-btn pj-btn-primary">
              Read the issue <Icon name="arrow" size={13} />
            </button>
            <button type="button" className="pj-btn pj-btn-ghost">Explore destinations</button>
          </div>

          <div className="pj-rise pj-rise-5 mt-12 flex flex-wrap items-center gap-7">
            <div>
              <p className="pj-display text-[28px] leading-none text-[var(--pj-text)]">12,400</p>
              <p className="pj-sans mt-1 text-[11px] uppercase tracking-[0.24em] text-[var(--pj-text-3)]">readers</p>
            </div>
            <span className="h-9 w-px bg-[var(--pj-border)]" />
            <div>
              <p className="pj-display text-[28px] leading-none text-[var(--pj-text)]">48</p>
              <p className="pj-sans mt-1 text-[11px] uppercase tracking-[0.24em] text-[var(--pj-text-3)]">issues</p>
            </div>
            <span className="h-9 w-px bg-[var(--pj-border)]" />
            <div>
              <p className="pj-display text-[28px] leading-none text-[var(--pj-text)]">26</p>
              <p className="pj-sans mt-1 text-[11px] uppercase tracking-[0.24em] text-[var(--pj-text-3)]">countries</p>
            </div>
          </div>
        </div>

        {/* Right — polaroid collage */}
        <div className="relative flex-1 min-w-0">
          <div className="relative mx-auto h-[460px] w-full max-w-[480px]">
            {/* Polaroid 1 — Paris */}
            <div className="pj-polaroid pj-zoom-in pj-float-a absolute" style={{ top: '6%', left: '4%', width: '54%', transform: 'rotate(-4deg)' }}>
              <div className="aspect-[5/6] overflow-hidden rounded-[3px]">
                <PolaroidScene kind="paris" />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <p className="pj-editorial text-[13px] italic text-[var(--pj-text)]">Paris, France</p>
                <p className="pj-sans text-[10px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">No. 12</p>
              </div>
            </div>

            {/* Polaroid 2 — Santorini */}
            <div className="pj-polaroid pj-zoom-in pj-float-b absolute" style={{ top: '12%', right: '0%', width: '50%', transform: 'rotate(3deg)', animationDelay: '0.18s' }}>
              <div className="aspect-[4/5] overflow-hidden rounded-[3px]">
                <PolaroidScene kind="santorini" />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <p className="pj-editorial text-[13px] italic text-[var(--pj-text)]">Santorini, GR</p>
                <p className="pj-sans text-[10px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">No. 11</p>
              </div>
            </div>

            {/* Polaroid 3 — Bali */}
            <div className="pj-polaroid pj-zoom-in pj-float-c absolute" style={{ bottom: '0%', left: '18%', width: '52%', transform: 'rotate(-1.5deg)', animationDelay: '0.30s' }}>
              <div className="aspect-[5/6] overflow-hidden rounded-[3px]">
                <PolaroidScene kind="bali" />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <p className="pj-editorial text-[13px] italic text-[var(--pj-text)]">Ubud, Bali</p>
                <p className="pj-sans text-[10px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">No. 12</p>
              </div>
            </div>

            {/* Polaroid 4 — Tokyo (peek) */}
            <div className="pj-polaroid pj-zoom-in absolute" style={{ top: '52%', right: '10%', width: '34%', transform: 'rotate(6deg)', animationDelay: '0.42s' }}>
              <div className="aspect-square overflow-hidden rounded-[3px]">
                <PolaroidScene kind="tokyo" />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <p className="pj-editorial text-[11px] italic text-[var(--pj-text)]">Tokyo, JP</p>
                <p className="pj-sans text-[9px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">No. 11</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/RouteSection.js': `import React from 'react';
import Icon from './Icon';
import { DESTINATIONS, MAP_POINTS, ROUTE_PATH } from '../data/paloma';

const TONE_PIN = {
  blush:    { fill: '#EBC1BD', stroke: '#D78F89' },
  peach:    { fill: '#F4C4A1', stroke: '#E09969' },
  sage:     { fill: '#C5D5C0', stroke: '#8FAB89' },
  sky:      { fill: '#C8D9E0', stroke: '#8FB1BD' },
  lavender: { fill: '#D4C5DF', stroke: '#A18AB8' },
};

/** Soft pastel illustrated world map (flat, decorative, not interactive globe). */
export default function RouteSection({ onSelect }) {
  return (
    <section className="bg-[var(--pj-cream)]" style={{ borderTop: '1px solid var(--pj-border)', borderBottom: '1px solid var(--pj-border)' }}>
      <div className="pj-section">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
          {/* Map */}
          <div>
            <p className="pj-eyebrow">The route</p>
            <h2 className="pj-headline mt-3 text-[44px] sm:text-[60px]">
              A spring journey, <span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>traced softly.</span>
            </h2>
            <p className="pj-editorial mt-4 max-w-[640px] text-[16.5px] leading-[1.55] text-[var(--pj-text-2)]">
              From the rive gauche to the rice terraces. Eight destinations, twelve writers, and a route that does not, anywhere, hurry.
            </p>

            <div className="mt-9 overflow-hidden rounded-[28px] border border-[var(--pj-border)] bg-[#FDFAF5]" style={{ boxShadow: '0 18px 40px -22px rgba(42,37,32,0.25)' }}>
              <svg viewBox="0 0 1600 760" preserveAspectRatio="xMidYMid meet" className="block w-full">
                <defs>
                  <linearGradient id="rs-paper" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FBF7F2" />
                    <stop offset="100%" stopColor="#F5EFE6" />
                  </linearGradient>
                  <pattern id="rs-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#E8DBC6" strokeWidth="0.4" />
                  </pattern>
                  <linearGradient id="rs-land" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#DCE6D5" />
                    <stop offset="100%" stopColor="#C5D5C0" />
                  </linearGradient>
                </defs>

                <rect width="1600" height="760" fill="url(#rs-paper)" />
                <rect width="1600" height="760" fill="url(#rs-grid)" opacity="0.35" />

                {/* Continent silhouettes — pastel sage */}
                <g fill="url(#rs-land)" stroke="#A8BFA1" strokeWidth="1" opacity="0.92">
                  {/* North America */}
                  <path d="M 180 200 C 240 170, 350 165, 420 195 C 500 220, 540 270, 540 320 C 540 380, 500 420, 450 450 C 400 470, 340 470, 300 450 C 240 420, 200 360, 195 310 C 190 270, 185 230, 180 200 Z" />
                  {/* South America */}
                  <path d="M 420 460 C 440 450, 475 465, 500 510 C 520 555, 525 610, 510 660 C 495 700, 475 720, 450 720 C 425 710, 410 670, 410 620 C 412 560, 415 500, 420 460 Z" />
                  {/* Europe */}
                  <path d="M 770 220 C 805 200, 855 200, 890 220 C 925 240, 940 270, 940 300 C 940 320, 920 340, 880 348 C 845 354, 820 350, 800 340 C 775 320, 770 290, 770 220 Z" />
                  {/* Africa */}
                  <path d="M 870 350 C 910 340, 960 360, 985 400 C 1010 450, 1020 510, 1010 560 C 1000 605, 975 640, 945 655 C 915 665, 890 645, 880 610 C 870 565, 870 510, 870 460 C 870 420, 870 380, 870 350 Z" />
                  {/* Asia */}
                  <path d="M 940 215 C 1020 195, 1110 195, 1200 218 C 1290 240, 1370 270, 1420 305 C 1455 330, 1455 360, 1410 380 C 1370 395, 1300 395, 1230 388 C 1160 380, 1090 370, 1030 360 C 990 350, 960 335, 945 315 C 935 285, 935 250, 940 215 Z" />
                  {/* Australia */}
                  <path d="M 1320 590 C 1370 580, 1430 590, 1470 615 C 1495 640, 1495 670, 1465 690 C 1430 710, 1380 715, 1340 695 C 1310 678, 1305 645, 1310 615 C 1310 605, 1315 597, 1320 590 Z" />
                </g>

                {/* Latitudes — extremely subtle */}
                <g stroke="#A99C8B" strokeWidth="0.4" fill="none" opacity="0.35">
                  <line x1="0" y1="380" x2="1600" y2="380" strokeDasharray="3 8" />
                  <line x1="0" y1="280" x2="1600" y2="280" strokeDasharray="3 8" />
                  <line x1="0" y1="480" x2="1600" y2="480" strokeDasharray="3 8" />
                </g>

                {/* Decorative dotted route arc */}
                <path d={ROUTE_PATH} className="pj-route-flow" stroke="#E9A294" />
                <path d={ROUTE_PATH} fill="none" stroke="rgba(233, 162, 148, 0.18)" strokeWidth="2.4" />

                {/* Pins */}
                <g>
                  {MAP_POINTS.map((m) => {
                    const dest = DESTINATIONS.find((d) => d.id === m.id);
                    if (!dest) return null;
                    const tone = TONE_PIN[dest.tone] || TONE_PIN.blush;
                    return (
                      <g key={m.id} style={{ cursor: 'pointer' }} onClick={() => onSelect(dest.id)}>
                        <circle cx={m.x} cy={m.y} r="10" fill={tone.fill} opacity="0.4" className="pj-pin-pulse" />
                        <circle cx={m.x} cy={m.y} r="7" fill={tone.fill} stroke={tone.stroke} strokeWidth="2" />
                        <circle cx={m.x} cy={m.y} r="2.4" fill="#2A2520" />
                        <rect x={m.x + 12} y={m.y - 13} width={dest.name.length * 9 + 14} height="24" rx="12" fill="#FFFFFF" stroke="#EDE3D4" strokeWidth="0.8" />
                        <text x={m.x + 22} y={m.y + 4} fontFamily="DM Sans, sans-serif" fontSize="12" fontWeight="500" fill="#2A2520">{dest.name}</text>
                      </g>
                    );
                  })}
                </g>

                {/* Compass mark bottom-left */}
                <g transform="translate(80, 660)" opacity="0.6">
                  <circle cx="0" cy="0" r="24" fill="none" stroke="#A99C8B" strokeWidth="0.8" />
                  <path d="M 0 -22 L 4 0 L 0 22 L -4 0 Z" fill="#2A2520" />
                  <text x="0" y="-30" textAnchor="middle" fontFamily="Italiana, serif" fontSize="13" fill="#2A2520">N</text>
                </g>
              </svg>
            </div>
          </div>

          {/* Right — itinerary list */}
          <aside>
            <p className="pj-eyebrow">This issue's path</p>
            <p className="pj-headline mt-2 text-[28px]">Eight stops.</p>

            <ol className="mt-7 space-y-3.5">
              {MAP_POINTS.map((m, i) => {
                const d = DESTINATIONS.find((x) => x.id === m.id);
                if (!d) return null;
                const tone = TONE_PIN[d.tone] || TONE_PIN.blush;
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(d.id)}
                      className="group flex w-full items-center gap-4 rounded-2xl border border-[var(--pj-border)] bg-white px-4 py-3 text-left transition hover:-translate-y-0.5"
                      style={{ boxShadow: '0 6px 14px -10px rgba(42,37,32,0.18)' }}
                    >
                      <span className="pj-sans text-[10.5px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]" style={{ width: 18, textAlign: 'right' }}>0{i + 1}</span>
                      <span className="block h-7 w-7 shrink-0 rounded-full border-2" style={{ background: tone.fill, borderColor: tone.stroke }} />
                      <span className="min-w-0 flex-1">
                        <span className="pj-display block text-[18px] leading-none text-[var(--pj-text)]">{d.name}</span>
                        <span className="pj-sans mt-1 block text-[11.5px] tracking-[0.06em] text-[var(--pj-text-3)]">{d.country} \\u00B7 {d.season}</span>
                      </span>
                      <Icon name="chev-right" size={14} className="shrink-0 text-[var(--pj-text-3)] transition group-hover:text-[var(--pj-text)]" />
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/CityArt.js': `import React from 'react';

/**
 * Soft pastel city illustrations — one per destination. Used in destination
 * cards, hero banners in the article view, and gallery thumbnails.
 */
const TONE = {
  blush:    { sky: '#F4D7D2', alt: '#EBC1BD', land: '#DCC8C4', accent: '#E9A294' },
  peach:    { sky: '#FAD9BC', alt: '#F4C4A1', land: '#E8D6BD', accent: '#E09969' },
  sage:     { sky: '#DCE6D5', alt: '#C5D5C0', land: '#B8CCB1', accent: '#8FAB89' },
  sky:      { sky: '#DCE7EC', alt: '#C8D9E0', land: '#B8CDD4', accent: '#8FB1BD' },
  lavender: { sky: '#E2D5EA', alt: '#D4C5DF', land: '#C5B6D0', accent: '#A18AB8' },
  cream:    { sky: '#F5EFE6', alt: '#EDE3D4', land: '#E8D9C2', accent: '#A99C8B' },
  sand:     { sky: '#FAEFE2', alt: '#E8D9C2', land: '#D6C2A4', accent: '#B89571' },
};

export default function CityArt({ kind, tone, className = '', style }) {
  const t = TONE[tone] || TONE.blush;

  return (
    <div className={className} style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
      <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" style={{ display: 'block', width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id={'cs-' + kind + '-' + tone} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={t.sky} />
            <stop offset="100%" stopColor={t.alt} />
          </linearGradient>
        </defs>
        <rect width="400" height="300" fill={'url(#cs-' + kind + '-' + tone + ')'} />

        {/* Sun / moon */}
        <circle cx="320" cy="60" r="32" fill={t.accent} opacity="0.45" />

        {kind === 'paris' && (
          <g>
            {/* Hausmann roofline */}
            <g fill={t.land} opacity="0.85">
              <rect x="0"   y="180" width="80"  height="120" />
              <rect x="80"  y="160" width="60"  height="140" />
              <rect x="240" y="160" width="60"  height="140" />
              <rect x="300" y="180" width="100" height="120" />
            </g>
            {/* Eiffel */}
            <g transform="translate(170, 60)" fill={t.accent}>
              <path d="M0 200 L30 0 L60 200 Z" />
              <rect x="13" y="155" width="34" height="6" />
              <rect x="17" y="100" width="26" height="6" />
              <rect x="22" y="50"  width="16" height="6" />
            </g>
            <g fill={t.accent} opacity="0.6">
              <circle cx="40" cy="200" r="1.5" />
              <circle cx="100" cy="195" r="1.5" />
              <circle cx="270" cy="195" r="1.5" />
              <circle cx="320" cy="205" r="1.5" />
            </g>
          </g>
        )}

        {kind === 'tokyo' && (
          <g>
            <g fill={t.land} opacity="0.8">
              <rect x="0"   y="195" width="50"  height="105" />
              <rect x="50"  y="170" width="50"  height="130" />
              <rect x="100" y="150" width="60"  height="150" />
              <rect x="260" y="150" width="60"  height="150" />
              <rect x="320" y="170" width="50"  height="130" />
              <rect x="370" y="195" width="30"  height="105" />
            </g>
            <g transform="translate(180, 50)" fill={t.accent}>
              <path d="M0 220 L20 0 L40 220 Z" />
              <rect x="6" y="170" width="28" height="8" />
              <rect x="10" y="100" width="20" height="6" />
            </g>
            {/* Sakura */}
            <g fill="#EBC1BD" opacity="0.85">
              <circle cx="60" cy="40" r="3.5" />
              <circle cx="110" cy="25" r="3" />
              <circle cx="350" cy="30" r="3.5" />
              <circle cx="380" cy="60" r="3" />
            </g>
          </g>
        )}

        {kind === 'seoul' && (
          <g>
            {/* Mountains */}
            <path d="M 0 200 L 80 130 L 160 170 L 240 110 L 320 160 L 400 130 L 400 300 L 0 300 Z" fill={t.land} opacity="0.8" />
            {/* N Seoul tower */}
            <g transform="translate(190, 65)" fill={t.accent}>
              <path d="M16 200 L20 30 L26 30 L30 200 Z" />
              <rect x="13" y="14" width="20" height="14" rx="3" />
              <rect x="20" y="0" width="6" height="14" />
            </g>
            {/* Hanok roof tiles */}
            <g fill={t.accent} opacity="0.7">
              <path d="M 50 230 L 90 215 L 130 230 L 130 250 L 50 250 Z" />
              <path d="M 270 240 L 310 225 L 350 240 L 350 260 L 270 260 Z" />
            </g>
          </g>
        )}

        {kind === 'bali' && (
          <g>
            {/* Terraced rice */}
            <g fill={t.land}>
              <path d="M0 220 L400 210 L400 300 L0 300 Z" />
              <path d="M0 200 L400 190 L400 215 L0 225 Z" opacity="0.78" />
              <path d="M0 180 L400 170 L400 195 L0 205 Z" opacity="0.55" />
              <path d="M0 160 L400 150 L400 175 L0 185 Z" opacity="0.32" />
            </g>
            {/* Palms */}
            <g fill={t.accent} opacity="0.85">
              <rect x="80" y="125" width="3" height="100" />
              <path d="M82 125 Q 50 110 35 125 Z" />
              <path d="M82 125 Q 114 108 130 124 Z" />
              <path d="M82 125 Q 68 95 56 80 Z" />
              <path d="M82 125 Q 100 92 110 80 Z" />
              <rect x="310" y="140" width="3" height="85" />
              <path d="M312 140 Q 280 125 264 140 Z" />
              <path d="M312 140 Q 344 124 358 140 Z" />
              <path d="M312 140 Q 298 110 290 95 Z" />
            </g>
          </g>
        )}

        {kind === 'santorini' && (
          <g>
            {/* Sea */}
            <rect x="0" y="200" width="400" height="100" fill={t.alt} opacity="0.7" />
            <g stroke="#FFFFFF" strokeWidth="0.7" fill="none" opacity="0.7">
              <path d="M 0 220 Q 100 226 200 220 T 400 220" />
              <path d="M 0 250 Q 100 256 200 250 T 400 250" />
            </g>
            {/* Cubes */}
            <g fill="#FFFFFF">
              <rect x="30" y="130" width="60" height="80" />
              <rect x="90" y="100" width="80" height="110" />
              <rect x="190" y="140" width="60" height="70" />
              <rect x="270" y="120" width="60" height="90" />
              <rect x="335" y="135" width="55" height="75" />
            </g>
            {/* Domes */}
            <g fill={t.accent} opacity="0.92">
              <ellipse cx="60" cy="130" rx="28" ry="14" />
              <ellipse cx="130" cy="100" rx="35" ry="16" />
              <ellipse cx="220" cy="140" rx="28" ry="13" />
              <ellipse cx="300" cy="120" rx="28" ry="14" />
              <ellipse cx="362" cy="135" rx="25" ry="13" />
            </g>
          </g>
        )}

        {kind === 'dubai' && (
          <g>
            {/* Dunes */}
            <path d="M 0 220 Q 100 200 200 220 T 400 220 L 400 300 L 0 300 Z" fill={t.land} opacity="0.85" />
            <path d="M 0 240 Q 120 220 240 240 T 400 240 L 400 300 L 0 300 Z" fill={t.accent} opacity="0.35" />
            {/* Burj */}
            <g transform="translate(180, 35)" fill={t.accent}>
              <rect x="14" y="180" width="20" height="50" />
              <rect x="11" y="120" width="26" height="60" />
              <rect x="8"  y="60"  width="32" height="60" />
              <rect x="5"  y="20"  width="38" height="40" />
              <polygon points="14,20 24,0 34,20" />
            </g>
            {/* Other buildings */}
            <g fill={t.land} opacity="0.75">
              <rect x="40"  y="170" width="40" height="60" />
              <rect x="80"  y="150" width="50" height="80" />
              <rect x="270" y="160" width="40" height="70" />
              <rect x="320" y="140" width="50" height="90" />
            </g>
          </g>
        )}

        {kind === 'switzerland' && (
          <g>
            {/* Mountains */}
            <path d="M 0 240 L 50 150 L 120 200 L 200 100 L 280 180 L 350 130 L 400 200 L 400 300 L 0 300 Z" fill={t.alt} opacity="0.85" />
            {/* Snow caps */}
            <g fill="#FFFFFF" opacity="0.8">
              <path d="M 200 100 L 220 130 L 180 130 Z" />
              <path d="M 350 130 L 365 155 L 335 155 Z" />
              <path d="M 50 150 L 65 170 L 35 170 Z" />
            </g>
            {/* Cabin */}
            <g transform="translate(150, 215)" fill={t.accent}>
              <rect x="0" y="10" width="50" height="35" />
              <polygon points="0,10 25,-5 50,10" />
              <rect x="20" y="22" width="10" height="22" fill={t.sky} />
            </g>
            {/* Trees */}
            <g fill={t.accent} opacity="0.85">
              <polygon points="60,220 70,250 50,250" />
              <polygon points="80,225 90,255 70,255" />
              <polygon points="280,230 290,260 270,260" />
              <polygon points="310,220 320,250 300,250" />
            </g>
          </g>
        )}

        {kind === 'new-york' && (
          <g>
            <g fill={t.land} opacity="0.78">
              <rect x="0"   y="170" width="40" height="130" />
              <rect x="40"  y="140" width="44" height="160" />
              <rect x="84"  y="120" width="36" height="180" />
              <rect x="120" y="80"  width="42" height="220" />
              <polygon points="141,80 148,55 155,80" />
              <rect x="162" y="100" width="50" height="200" />
              <rect x="212" y="60"  width="48" height="240" />
              <polygon points="236,60 242,30 248,60" />
              <rect x="260" y="120" width="40" height="180" />
              <rect x="300" y="100" width="40" height="200" />
              <rect x="340" y="140" width="60" height="160" />
            </g>
            <g fill={t.accent} opacity="0.6">
              <circle cx="50" cy="200" r="1.6" />
              <circle cx="100" cy="170" r="1.4" />
              <circle cx="100" cy="220" r="1.4" />
              <circle cx="140" cy="130" r="1.4" />
              <circle cx="140" cy="200" r="1.4" />
              <circle cx="230" cy="120" r="1.4" />
              <circle cx="230" cy="200" r="1.4" />
              <circle cx="320" cy="160" r="1.4" />
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}
`,

  'src/components/Destinations.js': `import React from 'react';
import Icon from './Icon';
import { DESTINATIONS } from '../data/paloma';
import CityArt from './CityArt';

const CHIP_CLASS = {
  blush: 'pj-chip-blush',
  peach: 'pj-chip-warm',
  sage: 'pj-chip-sage',
  sky: 'pj-chip-sky',
  lavender: 'pj-chip-lav',
};

function DestinationCard({ d, onSelect, size = 'normal' }) {
  const chipClass = CHIP_CLASS[d.tone] || 'pj-chip-warm';
  const ratio = size === 'large' ? '4/5' : size === 'wide' ? '16/10' : '4/3';
  return (
    <button
      type="button"
      onClick={() => onSelect(d.id)}
      className="pj-card group block w-full text-left"
    >
      <div className="pj-card-img" style={{ aspectRatio: ratio }}>
        <CityArt kind={d.hero} tone={d.tone} className="absolute inset-0" />
        <div className="absolute left-4 top-4">
          <span className={'pj-chip ' + chipClass}>{d.chip}</span>
        </div>
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="pj-sans text-[10.5px] uppercase tracking-[0.24em] text-white/85">{d.country}</p>
            <p className="pj-display text-[26px] leading-none text-white drop-shadow-sm sm:text-[30px]">{d.name}</p>
          </div>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/90 text-[var(--pj-text)] transition group-hover:bg-white">
            <Icon name="arrow-up" size={14} />
          </span>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        <p className="pj-eyebrow">{d.eyebrow}</p>
        <p className="pj-editorial pj-clamp-3 mt-2.5 text-[15px] leading-[1.55] text-[var(--pj-text-2)]">{d.teaser}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="pj-sans text-[11.5px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">{d.article.author}</span>
          <span className="pj-sans inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">
            <Icon name="clock" size={11} /> {d.article.readMin} min
          </span>
        </div>
      </div>
    </button>
  );
}

export default function Destinations({ onSelect }) {
  const featured = DESTINATIONS[0];
  const grid = DESTINATIONS.slice(1);
  return (
    <section id="Destinations">
      <div className="pj-section">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="pj-eyebrow">Featured destinations</p>
            <h2 className="pj-headline mt-3 text-[44px] sm:text-[60px]">
              Where we&apos;re going <span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>this spring.</span>
            </h2>
          </div>
          <p className="pj-editorial max-w-[420px] text-[15px] leading-[1.55] text-[var(--pj-text-2)]">
            Eight destinations across the Atlantic, the Mediterranean, and the Pacific. One slow letter from each.
          </p>
        </div>

        {/* Asymmetric layout: 1 large feature + 3 in column on right at large screens */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:gap-7">
          <div className="lg:col-span-7">
            <DestinationCard d={featured} onSelect={onSelect} size="large" />
          </div>
          <div className="grid gap-5 lg:col-span-5 lg:grid-cols-1 lg:gap-6 sm:grid-cols-2">
            {grid.slice(0, 3).map((d) => (
              <DestinationCard key={d.id} d={d} onSelect={onSelect} size="wide" />
            ))}
          </div>
        </div>

        {/* Second row — remaining 4 in a 4-col grid */}
        <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {grid.slice(3).map((d) => (
            <DestinationCard key={d.id} d={d} onSelect={onSelect} />
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/StoriesGrid.js': `import React from 'react';
import Icon from './Icon';
import { DESTINATIONS } from '../data/paloma';
import CityArt from './CityArt';

export default function StoriesGrid({ onSelect }) {
  const lead = DESTINATIONS[1]; // Tokyo
  const right = [DESTINATIONS[2], DESTINATIONS[4], DESTINATIONS[6]]; // Seoul, Santorini, Switzerland
  const bottom = [DESTINATIONS[5], DESTINATIONS[7]]; // Dubai, NYC

  return (
    <section id="Journals" className="bg-[var(--pj-cream)]" style={{ borderTop: '1px solid var(--pj-border)', borderBottom: '1px solid var(--pj-border)' }}>
      <div className="pj-section">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="pj-eyebrow">From the desks</p>
            <h2 className="pj-headline mt-3 text-[44px] sm:text-[60px]">Latest <span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>journals.</span></h2>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="pj-icon-btn" aria-label="Previous"><Icon name="chev-left" size={14} /></button>
            <button type="button" className="pj-icon-btn" aria-label="Next"><Icon name="chev-right" size={14} /></button>
            <button type="button" className="pj-btn pj-btn-soft">All journals <Icon name="arrow" size={13} /></button>
          </div>
        </div>

        {/* Pinterest-style asymmetry: 1 lead + 3 right column */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:gap-7">
          {/* Lead — large */}
          <article className="pj-card group lg:col-span-7" onClick={() => onSelect(lead.id)} style={{ cursor: 'pointer' }}>
            <div className="pj-card-img" style={{ aspectRatio: '16/11' }}>
              <CityArt kind={lead.hero} tone={lead.tone} className="absolute inset-0" />
              <div className="absolute left-5 top-5">
                <span className="pj-chip pj-chip-dark">Lead story</span>
              </div>
              <div className="absolute bottom-5 left-5 right-5">
                <p className="pj-eyebrow" style={{ color: 'rgba(255,255,255,0.78)' }}>{lead.eyebrow}</p>
                <h3 className="pj-headline pj-clamp-2 mt-2 text-[36px] leading-tight text-white sm:text-[44px]">{lead.article.title}</h3>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 p-5 sm:p-6">
              <div>
                <p className="pj-sans text-[12px] text-[var(--pj-text-2)]">by {lead.article.author} \\u00B7 {lead.article.role}</p>
                <p className="pj-sans mt-1 text-[10.5px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]">{lead.article.date} \\u00B7 {lead.article.readMin} min</p>
              </div>
              <span className="pj-btn pj-btn-soft shrink-0">Read journal <Icon name="arrow" size={13} /></span>
            </div>
          </article>

          {/* Right column */}
          <div className="space-y-5 lg:col-span-5">
            {right.map((d) => (
              <article key={d.id} className="pj-card group flex w-full overflow-hidden" onClick={() => onSelect(d.id)} style={{ cursor: 'pointer' }}>
                <div className="pj-card-img relative w-[42%] shrink-0" style={{ minHeight: 156 }}>
                  <CityArt kind={d.hero} tone={d.tone} className="absolute inset-0" />
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-between p-4 sm:p-5">
                  <div className="min-w-0">
                    <p className="pj-eyebrow">{d.chip}</p>
                    <p className="pj-display pj-clamp-2 mt-2 text-[20px] leading-tight text-[var(--pj-text)]">{d.article.title}</p>
                    <p className="pj-editorial pj-clamp-2 mt-2 text-[13.5px] leading-[1.5] text-[var(--pj-text-2)]">{d.teaser}</p>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="pj-sans text-[11px] tracking-[0.06em] text-[var(--pj-text-3)]">{d.article.author}</span>
                    <span className="pj-sans inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">
                      <Icon name="clock" size={10} /> {d.article.readMin}m
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Bottom row — 2 wide */}
        <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:gap-7">
          {bottom.map((d) => (
            <article key={d.id} className="pj-card group" onClick={() => onSelect(d.id)} style={{ cursor: 'pointer' }}>
              <div className="pj-card-img" style={{ aspectRatio: '16/9' }}>
                <CityArt kind={d.hero} tone={d.tone} className="absolute inset-0" />
                <div className="absolute right-5 top-5">
                  <span className="pj-chip" style={{ background: 'rgba(255,255,255,0.85)' }}>{d.chip}</span>
                </div>
              </div>
              <div className="p-5 sm:p-6">
                <p className="pj-eyebrow">{d.eyebrow}</p>
                <p className="pj-display mt-2 text-[24px] leading-tight text-[var(--pj-text)]">{d.article.title}</p>
                <p className="pj-editorial pj-clamp-2 mt-2 text-[14px] leading-[1.55] text-[var(--pj-text-2)]">{d.teaser}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/JournalHighlights.js': `import React from 'react';
import { HIGHLIGHTS } from '../data/paloma';

const TONE_BG = {
  blush: 'rgba(235, 193, 189, 0.4)',
  sage: 'rgba(197, 213, 192, 0.45)',
  sky: 'rgba(200, 217, 224, 0.45)',
  peach: 'rgba(244, 196, 161, 0.4)',
  lavender: 'rgba(212, 197, 223, 0.45)',
};

export default function JournalHighlights() {
  return (
    <section>
      <div className="pj-section">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="pj-eyebrow">From the journal</p>
            <h2 className="pj-headline mt-3 text-[44px] sm:text-[60px]">Lines we <span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>kept.</span></h2>
          </div>
          <p className="pj-editorial max-w-[420px] text-[15px] leading-[1.55] text-[var(--pj-text-2)]">
            Three notebook lines from this issue. We trim each story down to the sentence we wished we had written ourselves.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {HIGHLIGHTS.map((h, i) => (
            <figure
              key={h.by + h.city}
              className="pj-rise relative flex flex-col justify-between overflow-hidden rounded-[28px] p-7 sm:p-8"
              style={{
                background: TONE_BG[h.tone] || TONE_BG.blush,
                border: '1px solid rgba(255,255,255,0.6)',
                animationDelay: (0.08 + i * 0.08) + 's',
                minHeight: 320,
              }}
            >
              <span className="pj-display absolute -left-2 top-2 text-[180px] leading-none text-[var(--pj-text)] opacity-[0.10]">&ldquo;</span>
              <blockquote className="pj-editorial relative text-[18.5px] leading-[1.55] text-[var(--pj-text)] sm:text-[20px]">
                {h.quote}
              </blockquote>
              <figcaption className="mt-7 flex items-center justify-between">
                <div>
                  <p className="pj-display text-[16px] text-[var(--pj-text)]">{h.by}</p>
                  <p className="pj-sans mt-0.5 text-[11px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]">From {h.city}</p>
                </div>
                <span className="pj-display text-[28px] text-[var(--pj-text)]">{String(i + 1).padStart(2, '0')}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/PhotoEssay.js': `import React from 'react';
import { PHOTO_ESSAY } from '../data/paloma';
import CityArt from './CityArt';

const KIND_FOR_TONE = {
  blush: 'paris',
  peach: 'dubai',
  sage: 'bali',
  sky: 'santorini',
  lavender: 'new-york',
  cream: 'switzerland',
};

export default function PhotoEssay() {
  return (
    <section id="Photo Essays" className="bg-[var(--pj-cream)]" style={{ borderTop: '1px solid var(--pj-border)', borderBottom: '1px solid var(--pj-border)' }}>
      <div className="pj-section">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="pj-eyebrow">Photo essay</p>
            <h2 className="pj-headline mt-3 text-[44px] sm:text-[60px]">A spring of <span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>frames.</span></h2>
          </div>
          <p className="pj-editorial max-w-[420px] text-[15px] leading-[1.55] text-[var(--pj-text-2)]">
            Eight quiet frames from this issue. Picked by the editors. Sequenced like a small Sunday-morning book.
          </p>
        </div>

        {/* Pinterest-style masonry using CSS columns (each tile sets its own height) */}
        <div style={{ columnGap: 20 }} className="md:columns-3 lg:columns-4 columns-2">
          {PHOTO_ESSAY.map((p, i) => (
            <figure
              key={p.label + i}
              className="pj-card pj-rise relative mb-5 inline-block w-full overflow-hidden"
              style={{ animationDelay: (0.04 + i * 0.04) + 's', breakInside: 'avoid' }}
            >
              <div className="pj-card-img" style={{ height: p.h }}>
                <CityArt kind={KIND_FOR_TONE[p.tone] || 'paris'} tone={p.tone} className="absolute inset-0" />
              </div>
              <figcaption className="flex items-center justify-between px-4 py-3">
                <p className="pj-editorial italic text-[13.5px] text-[var(--pj-text)]">{p.label}</p>
                <p className="pj-sans text-[10px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]">0{i + 1}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/CreatorNotes.js': `import React from 'react';
import Icon from './Icon';
import { CREATORS } from '../data/paloma';

const TONE_AVATAR = {
  blush:    'linear-gradient(135deg, #EBC1BD 0%, #F4D7D2 100%)',
  sage:     'linear-gradient(135deg, #C5D5C0 0%, #DCE6D5 100%)',
  sky:      'linear-gradient(135deg, #C8D9E0 0%, #DCE7EC 100%)',
  lavender: 'linear-gradient(135deg, #D4C5DF 0%, #E2D5EA 100%)',
  peach:    'linear-gradient(135deg, #F4C4A1 0%, #FAD9BC 100%)',
};

export default function CreatorNotes() {
  return (
    <section>
      <div className="pj-section">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="pj-eyebrow">Notes from the field</p>
            <h2 className="pj-headline mt-3 text-[44px] sm:text-[60px]">Who is <span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>writing.</span></h2>
          </div>
          <p className="pj-editorial max-w-[420px] text-[15px] leading-[1.55] text-[var(--pj-text-2)]">
            Twelve writers. Four on the road right now. A note from each \\u2014 hand-written in their notebook, mailed in.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {CREATORS.map((c, i) => (
            <article
              key={c.name}
              className="pj-rise grid grid-cols-[80px_1fr] items-start gap-5 rounded-[28px] border border-[var(--pj-border)] bg-white p-6 sm:grid-cols-[96px_1fr] sm:gap-6 sm:p-7"
              style={{ animationDelay: (0.05 + i * 0.06) + 's', boxShadow: '0 14px 30px -22px rgba(42,37,32,0.25)' }}
            >
              <div className="flex h-[80px] w-[80px] items-center justify-center rounded-full sm:h-[96px] sm:w-[96px]" style={{ background: TONE_AVATAR[c.tone] || TONE_AVATAR.blush }}>
                <span className="pj-display text-[28px] text-[var(--pj-text)]">
                  {c.name.split(' ').map((s) => s[0]).slice(0, 2).join('')}
                </span>
              </div>
              <div className="min-w-0">
                <p className="pj-display text-[22px] leading-none text-[var(--pj-text)]">{c.name}</p>
                <p className="pj-sans mt-1 text-[11.5px] uppercase tracking-[0.2em] text-[var(--pj-text-3)]">{c.role}</p>
                <p className="pj-editorial mt-4 text-[15px] leading-[1.6] text-[var(--pj-text-2)]">{c.note}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="pj-sans inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">
                    <Icon name="pin" size={12} /> {c.place}
                  </span>
                  <span className="pj-sans text-[11px] uppercase tracking-[0.22em] text-[var(--pj-text-3)] cursor-pointer hover:text-[var(--pj-text)]">Read all journals \\u2192</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Newsletter.js': `import React from 'react';
import Icon from './Icon';

export default function Newsletter() {
  return (
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(244,196,161,0.42) 0%, rgba(235,193,189,0.35) 45%, rgba(212,197,223,0.32) 100%)' }}>
      <div className="pj-section">
        <div className="mx-auto max-w-[820px] text-center">
          <span className="pj-chip pj-chip-warm">Friday journal</span>
          <h2 className="pj-headline mt-5 text-[52px] sm:text-[72px]">
            The slow road, <br className="hidden sm:block" />
            <span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>in your inbox.</span>
          </h2>
          <p className="pj-editorial mx-auto mt-6 max-w-[520px] text-[16.5px] leading-[1.55] text-[var(--pj-text-2)]">
            One travel story every Friday morning. Eight minutes long. No partnerships. No ads. Just the road.
          </p>

          <form className="mx-auto mt-10 flex max-w-[480px] flex-col items-stretch gap-3 sm:flex-row" onSubmit={(e) => e.preventDefault()}>
            <label className="flex w-full items-center gap-3 rounded-full border border-[var(--pj-border)] bg-white px-5 py-3" style={{ boxShadow: '0 14px 30px -22px rgba(42,37,32,0.25)' }}>
              <Icon name="mail" size={15} className="shrink-0 text-[var(--pj-text-3)]" />
              <input type="email" placeholder="your.address@email.com" className="pj-sans flex-1 border-0 bg-transparent text-[14px] text-[var(--pj-text)] outline-none placeholder:text-[var(--pj-text-3)]" />
            </label>
            <button type="submit" className="pj-btn pj-btn-primary justify-center sm:justify-start">
              Subscribe <Icon name="arrow" size={13} />
            </button>
          </form>

          <p className="pj-sans mt-5 text-[11px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]">
            Join 12,400 readers \\u00B7 Unsubscribe anytime
          </p>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Footer.js': `import React from 'react';
import Icon from './Icon';

const COLUMNS = [
  {
    title: 'The Journal',
    items: ['Latest issue', 'Archive', 'Featured stories', 'Photo essays', 'Travel notes'],
  },
  {
    title: 'Destinations',
    items: ['Paris', 'Tokyo', 'Bali', 'Santorini', 'Switzerland', 'New York'],
  },
  {
    title: 'Studio',
    items: ['About Paloma', 'Writers', 'Contact', 'Submissions', 'Press'],
  },
];

export default function Footer() {
  return (
    <footer style={{ background: '#2A2520', color: '#FBF7F2' }}>
      <div className="pj-section" style={{ paddingTop: 80, paddingBottom: 28 }}>
        <div className="grid grid-cols-1 gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          {/* Brand */}
          <div>
            <p className="pj-display text-[40px] tracking-[0.08em] text-[#FBF7F2]">Paloma</p>
            <p className="pj-editorial mt-2 italic text-[14px] text-[#C9B898]" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>A Modern Travel Journal</p>
            <p className="pj-editorial mt-6 max-w-[380px] text-[14px] leading-[1.65] text-[#C9B898]">
              A slow letter from the road, every Friday morning. Twelve writers, eight cities, one quiet idea: travel as a craft.
            </p>
            <div className="mt-7 flex items-center gap-2">
              <a href="#" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#5B4F44] text-[#FBF7F2] transition hover:border-[#FBF7F2]" aria-label="Instagram"><Icon name="instagram" size={14} /></a>
              <a href="#" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#5B4F44] text-[#FBF7F2] transition hover:border-[#FBF7F2]" aria-label="Pinterest"><Icon name="pinterest" size={14} /></a>
              <a href="#" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#5B4F44] text-[#FBF7F2] transition hover:border-[#FBF7F2]" aria-label="Twitter"><Icon name="twitter" size={14} /></a>
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title}>
              <p className="pj-sans text-[11px] uppercase tracking-[0.32em] text-[#C9B898]">{col.title}</p>
              <ul className="mt-5 space-y-3">
                {col.items.map((item) => (
                  <li key={item}>
                    <a href={'#' + item} className="pj-editorial italic text-[15px] text-[#FBF7F2] transition hover:text-[#F4C4A1]" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>{item}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-3 border-t border-[#3F362E] pt-7 sm:flex-row">
          <p className="pj-sans text-[11px] uppercase tracking-[0.22em] text-[#A99C8B]">
            © 2026 Paloma Studio \\u00B7 Volume XII \\u00B7 Spring issue
          </p>
          <p className="pj-editorial italic text-[12px] text-[#A99C8B]" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>Made slowly, in Paris and Tokyo.</p>
        </div>
      </div>
    </footer>
  );
}
`,

  'src/components/ArticleView.js': `import React from 'react';
import Icon from './Icon';
import { DESTINATIONS } from '../data/paloma';
import CityArt from './CityArt';

const CHIP_CLASS = {
  blush: 'pj-chip-blush', peach: 'pj-chip-warm',
  sage: 'pj-chip-sage', sky: 'pj-chip-sky', lavender: 'pj-chip-lav',
};

export default function ArticleView({ destination, onClose, onSelect }) {
  const d = destination;
  const a = d.article;
  const related = (a.related || []).map((id) => DESTINATIONS.find((x) => x.id === id)).filter(Boolean);
  const chipClass = CHIP_CLASS[d.tone] || 'pj-chip-warm';

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[var(--pj-ivory)] pj-article-in">
      {/* Top bar */}
      <header className="sticky top-0 z-10 flex shrink-0 items-center justify-between gap-3 border-b border-[var(--pj-border)] bg-[rgba(251,247,242,0.92)] px-5 py-3.5 backdrop-blur sm:px-10">
        <div className="flex min-w-0 items-center gap-3">
          <span className="pj-display text-[20px] tracking-[0.1em] text-[var(--pj-text)]">Paloma</span>
          <span className="hidden h-5 w-px bg-[var(--pj-border)] sm:block" />
          <span className="pj-eyebrow hidden sm:inline">{a.author} \\u00B7 {a.role}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button type="button" className="pj-icon-btn" aria-label="Bookmark"><Icon name="bookmark" size={14} /></button>
          <button type="button" className="pj-icon-btn" aria-label="Share"><Icon name="share" size={14} /></button>
          <button type="button" onClick={onClose} className="pj-btn pj-btn-soft" aria-label="Close article">
            <Icon name="close" size={13} /> Close
          </button>
        </div>
      </header>

      <div className="pj-scroll min-w-0 flex-1 overflow-y-auto">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="relative h-[420px] w-full sm:h-[560px]">
            <CityArt kind={a.hero} tone={d.tone} className="absolute inset-0" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(251,247,242,0) 50%, rgba(251,247,242,0.85) 100%)' }} />
          </div>

          <div className="mx-auto max-w-[1320px] px-5 sm:px-10">
            <div className="-mt-[180px] sm:-mt-[240px]">
              <div className="max-w-[920px]">
                <span className={'pj-chip ' + chipClass}>{d.chip}</span>
                <h1 className="pj-headline mt-6 text-[44px] leading-[1.02] sm:text-[80px] md:text-[104px]">
                  {a.title}
                </h1>
                <p className="pj-editorial mt-7 max-w-[680px] text-[22px] leading-[1.45] text-[var(--pj-text-2)] sm:text-[26px]">
                  {a.dek}
                </p>

                <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-b border-[var(--pj-border)] py-5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: 'linear-gradient(135deg, #EBC1BD 0%, #F4D7D2 100%)' }}>
                      <span className="pj-display text-[13px] text-[var(--pj-text)]">
                        {a.author.split(' ').map((s) => s[0]).slice(0, 2).join('')}
                      </span>
                    </span>
                    <div>
                      <p className="pj-display text-[15.5px] text-[var(--pj-text)]">{a.author}</p>
                      <p className="pj-sans text-[10.5px] uppercase tracking-[0.2em] text-[var(--pj-text-3)]">{a.role}</p>
                    </div>
                  </div>
                  <span className="h-5 w-px bg-[var(--pj-border)]" />
                  <span className="pj-sans text-[12px] uppercase tracking-[0.18em] text-[var(--pj-text-2)]">{a.date}</span>
                  <span className="pj-sans inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] text-[var(--pj-text-2)]">
                    <Icon name="clock" size={12} /> {a.readMin} min read
                  </span>
                  <span className="pj-sans inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] text-[var(--pj-text-2)]">
                    <Icon name="pin" size={12} /> {d.name}, {d.country}
                  </span>
                  <span className="pj-chip pj-chip-dark ml-auto">Vol XII \\u00B7 Spring 2026</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="mx-auto max-w-[1320px] px-5 py-16 sm:px-10 sm:py-20">
          <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-12">
            <article className="md:col-span-8 md:col-start-2">
              <p className="pj-body">
                <span className="pj-display float-left mr-3 mt-1 text-[80px] leading-[0.85] text-[var(--pj-text)]">{a.body[0].charAt(0)}</span>
                {a.body[0].slice(1)}
              </p>

              {/* Pull quote between paragraphs */}
              <figure className="my-14 border-y border-[var(--pj-border)] py-10 text-center">
                <blockquote className="pj-display text-[28px] leading-[1.2] text-[var(--pj-text)] sm:text-[40px]">
                  &ldquo;<span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>{a.pullQuote}</span>&rdquo;
                </blockquote>
                <figcaption className="pj-sans mt-6 text-[11px] uppercase tracking-[0.24em] text-[var(--pj-text-3)]">
                  \\u2014 {a.pullQuoteBy}
                </figcaption>
              </figure>

              {a.body.slice(1).map((p, i) => (
                <p key={'p2-' + i} className="pj-body mt-6">{p}</p>
              ))}

              {/* Travel highlights */}
              <div className="mt-14 rounded-[28px] border border-[var(--pj-border)] bg-[var(--pj-cream)] p-7 sm:p-9">
                <div className="flex items-end justify-between gap-3">
                  <p className="pj-eyebrow">Travel highlights</p>
                  <span className="pj-sans text-[11px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]">04</span>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-x-7 gap-y-6 sm:grid-cols-2">
                  {a.highlights.map((h, i) => (
                    <div key={h.title}>
                      <div className="flex items-center gap-3">
                        <span className="pj-display text-[24px] text-[var(--pj-text)]">{String(i + 1).padStart(2, '0')}</span>
                        <span className="h-px flex-1 bg-[var(--pj-text-3)]" />
                      </div>
                      <p className="pj-display mt-3 text-[18px] text-[var(--pj-text)]">{h.title}</p>
                      <p className="pj-editorial mt-2 text-[14.5px] leading-[1.55] text-[var(--pj-text-2)]">{h.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            {/* Right rail */}
            <aside className="md:col-span-3 md:col-start-10 md:col-end-13">
              <div className="sticky top-24">
                <p className="pj-eyebrow">Field notes</p>
                <dl className="mt-5 space-y-4">
                  <div className="flex items-baseline justify-between gap-3 border-b border-[var(--pj-border)] pb-3">
                    <dt className="pj-sans text-[11px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">Season</dt>
                    <dd className="pj-editorial italic text-[14px] text-[var(--pj-text)]">{d.season}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 border-b border-[var(--pj-border)] pb-3">
                    <dt className="pj-sans text-[11px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">City</dt>
                    <dd className="pj-editorial italic text-[14px] text-[var(--pj-text)]">{d.name}, {d.country}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 border-b border-[var(--pj-border)] pb-3">
                    <dt className="pj-sans text-[11px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">Issue</dt>
                    <dd className="pj-editorial italic text-[14px] text-[var(--pj-text)]">Vol XII, Spring '26</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 border-b border-[var(--pj-border)] pb-3">
                    <dt className="pj-sans text-[11px] uppercase tracking-[0.18em] text-[var(--pj-text-3)]">Read</dt>
                    <dd className="pj-editorial italic text-[14px] text-[var(--pj-text)]">{a.readMin} minutes</dd>
                  </div>
                </dl>

                <p className="pj-eyebrow mt-10">Save / Share</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" className="pj-btn pj-btn-soft">Bookmark <Icon name="bookmark" size={12} /></button>
                  <button type="button" className="pj-btn pj-btn-soft">Share <Icon name="share" size={12} /></button>
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* Gallery */}
        <section className="bg-[var(--pj-cream)]" style={{ borderTop: '1px solid var(--pj-border)', borderBottom: '1px solid var(--pj-border)' }}>
          <div className="mx-auto max-w-[1320px] px-5 py-16 sm:px-10 sm:py-20">
            <div className="mb-9 flex items-end justify-between gap-3">
              <div>
                <p className="pj-eyebrow">A small photo essay</p>
                <h3 className="pj-headline mt-3 text-[36px] sm:text-[48px]">
                  Four <span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>frames.</span>
                </h3>
              </div>
              <span className="pj-sans text-[11px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]">{a.gallery.length} selects</span>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
              {a.gallery.map((g, i) => (
                <figure key={g.label + i} className="pj-card overflow-hidden">
                  <div className="pj-card-img" style={{ aspectRatio: '4/5' }}>
                    <CityArt kind={a.hero} tone={g.tone} className="absolute inset-0" />
                  </div>
                  <figcaption className="flex items-center justify-between px-4 py-3">
                    <p className="pj-editorial italic text-[13.5px] text-[var(--pj-text)]">{g.label}</p>
                    <p className="pj-sans text-[10px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]">0{i + 1}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section>
            <div className="mx-auto max-w-[1320px] px-5 py-16 sm:px-10 sm:py-20">
              <div className="mb-9 flex items-end justify-between gap-3">
                <div>
                  <p className="pj-eyebrow">Continue the road</p>
                  <h3 className="pj-headline mt-3 text-[36px] sm:text-[48px]">
                    Two more <span className="pj-editorial italic" style={{ fontFamily: 'PT Serif', fontStyle: 'italic' }}>stops.</span>
                  </h3>
                </div>
                <span className="pj-sans text-[11px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]">{related.length} journals</span>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:gap-7">
                {related.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onSelect(r.id)}
                    className="pj-card group overflow-hidden text-left"
                  >
                    <div className="pj-card-img" style={{ aspectRatio: '16/10' }}>
                      <CityArt kind={r.hero} tone={r.tone} className="absolute inset-0" />
                      <div className="absolute left-4 top-4">
                        <span className={'pj-chip ' + (CHIP_CLASS[r.tone] || 'pj-chip-warm')}>{r.chip}</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="pj-sans text-[10.5px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]">{r.country}</p>
                      <p className="pj-display mt-2 text-[26px] leading-tight text-[var(--pj-text)]">{r.article.title}</p>
                      <p className="pj-editorial pj-clamp-2 mt-2 text-[14px] leading-[1.55] text-[var(--pj-text-2)]">{r.teaser}</p>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-14 flex flex-col items-center gap-3">
                <button type="button" onClick={onClose} className="pj-btn pj-btn-primary">
                  Back to the journal <Icon name="arrow" size={13} />
                </button>
                <p className="pj-sans text-[11px] uppercase tracking-[0.22em] text-[var(--pj-text-3)]">Paloma \\u00B7 A Modern Travel Journal</p>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
`,
};
